import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pager } from '../../components/Pager';
import { SectorIntelFilterBar } from './SectorIntelFilterBar';
import { SectorIntelTable } from './SectorIntelTable';
import {
  useSectorIntelFilterOptions,
  useSectorIntelList,
} from './useSectorIntel';
import {
  ALL_SECTOR_TYPES,
  DEFAULT_SECTOR_INTEL_FILTERS,
  type SectorIntelFilters,
  type SectorType,
} from './sectorIntel.types';
import { downloadSectorIntelExport } from '../../api/sectorIntelApi';
import '../PendingCAEvents/pendingCa.css';
import './sectorIntel.css';

// 쿼리스트링으로 섹터모음 화면에서 건너온 초기 필터 반영.
// - sector_name: 문자열 그대로 (ILIKE)
// - sector_type: PRIMARY/SECONDARY/THEME 중 하나 (선택)
// - canonical_sector_id: 정확 일치 (선택)
function buildInitialFilters(params: URLSearchParams): SectorIntelFilters {
  const name = params.get('sector_name')?.trim() ?? '';
  const typeRaw = params.get('sector_type')?.trim().toUpperCase() ?? '';
  const sectorTypes: SectorType[] =
    typeRaw && (ALL_SECTOR_TYPES as string[]).includes(typeRaw)
      ? [typeRaw as SectorType]
      : [];
  const canonicalSectorId = params.get('canonical_sector_id')?.trim() ?? '';
  if (!name && sectorTypes.length === 0 && !canonicalSectorId) {
    return DEFAULT_SECTOR_INTEL_FILTERS;
  }
  return {
    ...DEFAULT_SECTOR_INTEL_FILTERS,
    sectorName: name,
    sectorTypes,
    canonicalSectorId,
  };
}

interface ToastItem {
  id: string;
  msg: string;
  tone: 'info' | 'success' | 'error' | 'warn';
}

export default function SectorIntelligencePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  // 마운트 시 1회만 쿼리스트링 해석 (이후 필터는 로컬 state 가 권위).
  const [filters, setFilters] = useState<SectorIntelFilters>(() =>
    buildInitialFilters(searchParams)
  );
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [exporting, setExporting] = useState(false);

  const pushToast = useCallback(
    (msg: string, tone: ToastItem['tone'] = 'info') => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, msg, tone }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4500);
    },
    []
  );

  const list = useSectorIntelList(filters);
  const options = useSectorIntelFilterOptions();

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  const pageInfo = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, filters.size)));
    return { totalPages, current: filters.page + 1 };
  }, [total, filters.size, filters.page]);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadSectorIntelExport(filters);
      pushToast('엑셀 다운로드 완료', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'export_failed';
      const [code, detail] = msg.split(':');
      if (code === 'export_too_large') {
        pushToast('결과가 너무 큽니다. 필터를 더 좁혀주세요.', 'error');
      } else {
        pushToast(detail ?? msg, 'error');
      }
    } finally {
      setExporting(false);
    }
  }, [exporting, filters, pushToast]);

  const jumpToPage = useCallback(
    (nextPage: number) => {
      const clamped = Math.max(0, Math.min(pageInfo.totalPages - 1, nextPage));
      if (clamped !== filters.page) {
        setFilters({ ...filters, page: clamped });
      }
    },
    [filters, pageInfo.totalPages]
  );

  return (
    <div className="pca-page">
      <SectorIntelFilterBar
        filters={filters}
        availableSources={options.data?.sources ?? []}
        onChange={setFilters}
        onExport={handleExport}
        exporting={exporting}
      />

      {list.isError && (
        <div className="pca-warning-banner">
          목록 로드 실패: {(list.error as Error).message}
        </div>
      )}

      <SectorIntelTable items={items} isLoading={list.isLoading} />

      <Pager
        page={filters.page}
        pageSize={filters.size}
        total={total}
        onPageChange={(p) => jumpToPage(p)}
        pageSizeOptions={[50, 100, 200, 500]}
        onPageSizeChange={(size) => setFilters({ ...filters, size, page: 0 })}
      />


      <div className="pca-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`pca-toast pca-toast--${t.tone}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}

